import { LocationsPublicApi } from '@up2stay/up2promo-api-client';
import type { Suggestion } from '@up2promo-setup-frontend/core/store/slices/input.slice';
import { useSetupStore } from '@up2promo-setup-frontend/core/store/setup.store';

/**
 * Fetch Google Places suggestions based on business name and optional address.
 * Makes parallel API calls for better results. Predictions only (no billable
 * Place Details): suggestions use just id, name and address.
 */
export async function setSuggestionsForGoogle(
  businessName: string,
  address?: string,
) {
  // Make API calls in parallel: one with name only, one with name+address (if address provided)
  const nameOnlyRequest = LocationsPublicApi.searchGooglePlaces({
    query: {
      input: businessName,
      establishmentPoiOnly: true,
      includeDetails: false,
    },
  });

  // If address is provided, also search with name+address
  const nameAndAddressRequest =
    address && address.trim().length > 0
      ? LocationsPublicApi.searchGooglePlaces({
          query: {
            input: `${businessName}, ${address}`,
            establishmentPoiOnly: true,
            includeDetails: false,
          },
        })
      : null;

  // Wait for all requests to complete
  const responses = await Promise.all([
    nameOnlyRequest,
    ...(nameAndAddressRequest ? [nameAndAddressRequest] : []),
  ]);

  // Collect all places from both responses
  const allPlaces: Array<{
    id: string;
    name: string;
    address: string;
    types?: string[];
  }> = [];

  // Add name+address results first (prioritize them)
  // responses[0] is name-only, responses[1] is name+address (if it exists)
  if (responses.length > 1) {
    allPlaces.push(...responses[1]!.data);
  }

  // Then add name-only results
  allPlaces.push(...responses[0]!.data);

  // Remove duplicates by place ID (keep first occurrence, which prioritizes name+address results)
  const seenIds = new Set<string>();
  const uniquePlaces = allPlaces.filter((place) => {
    if (seenIds.has(place.id)) {
      return false;
    }
    seenIds.add(place.id);
    return true;
  });

  // Take up to 4 places
  const places = uniquePlaces.slice(0, 4);

  const suggestions: Suggestion[] = places.map((place) => ({
    id: place.id,
    name: place.name,
    description: place.address,
    targetLink: `https://search.google.com/local/writereview?placeid=${place.id}`,
  }));

  const { setSuggestions } = useSetupStore.getState();
  setSuggestions(suggestions);
}
